<script setup lang="ts">
import { computed, ref } from 'vue';
import store from '@/store';
import CInput from '@/components/shared/CInput';
import CButton from '@/components/shared/CButton';
import { type CSelectItem, Primitive } from '@/components/shared/CSelect';
import { SPECIAL_CATEGORY_VALUES } from '@/components/DesignStudio/ImageEffectsProperties/types/presets';
import { PRESET_CATEGORIES } from '@/components/DesignStudio/ImageEffectsProperties/core/presets';

const searchInputRef = ref('');

const selectedCategoryValue = computed(() => store.getters['imageEffectsProperties/selectedCategoryValue']);
const hasActiveFilters = computed(() => store.getters['imageEffectsProperties/hasActiveFilters']);

const categoryOptions = computed(() => [
    { label: 'All Categories', value: SPECIAL_CATEGORY_VALUES.ALL },
    { label: 'Free', value: SPECIAL_CATEGORY_VALUES.FREE },
    ...PRESET_CATEGORIES.map((cat) => ({
        label: cat.label,
        value: cat.category
    }))
]);

const handleSearchInput = () => {
    store.dispatch('imageEffectsProperties/updateSearchQuery', searchInputRef.value);
};

const handleCategorySelectChange = (event: Event) => {
    const target = event.target as HTMLSelectElement;
    const selectedValue = target.value as CSelectItem<Primitive> | Primitive;
    store.dispatch('imageEffectsProperties/updateCategoryValue', selectedValue);
};

const handleClearFilters = () => {
    store.dispatch('imageEffectsProperties/clearFilters');
    searchInputRef.value = '';
};
</script>

<template>
    <div class="search-filters">
        <div class="search-and-categories d-flex align-items-end">
            <div class="search-input-container">
                <c-input
                    v-model="searchInputRef"
                    label="Search effects"
                    placeholder="Type to search..."
                    @input="handleSearchInput"
                >
                    <template #append>
                        <c-button
                            v-if="hasActiveFilters"
                            text="Clear filters"
                            variant="secondary"
                            size="sm"
                            @click="handleClearFilters"
                        />
                    </template>
                </c-input>
            </div>

            <div class="category-select">
                <select :value="selectedCategoryValue" class="category-dropdown" @change="handleCategorySelectChange">
                    <option v-for="option in categoryOptions" :key="option.value" :value="option.value">
                        {{ option.label }}
                    </option>
                </select>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.search-filters {
    margin-bottom: 16px;
}

.search-and-categories {
    gap: 16px;
}

.search-input-container {
    flex: 1;
    min-width: 250px;

    .search-input {
        width: 100%;
    }
}

.filter-actions {
    flex-shrink: 0;

    @media (max-width: 1024px) {
        align-self: flex-start;
    }
}

.category-select {
    min-width: 200px;
    flex-shrink: 0;

    @media (max-width: 1024px) {
        min-width: 150px;
    }

    .category-dropdown {
        padding: 8px 12px;
        border: 1px solid var(--gray-light);
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s ease;
        padding-right: 35px;
        width: 100%;
        height: 40px;

        &:hover {
            border-color: var(--primary-purple);
            box-shadow: 0 0 0 0.1rem rgba(0, 123, 255, 0.1);
        }

        &:focus {
            border-color: var(--primary-purple);
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        }

        option {
            padding: 8px 12px;
            color: #495057;
        }

        @media (max-width: 1024px) {
            font-size: 13px;
            padding: 6px 10px;
            padding-right: 30px;
        }
    }
}
</style>
